import React from 'react';
import { MapPin, Users, ArrowRight, Briefcase, Zap } from 'lucide-react';

/* Networking · pipeline row — clean full-width clickable company row.
   Matches the "Networking · sequencer redesign" pipeline-list artboard:
   logo tile, role link, location + contact count, overlapping avatar stack,
   progress bar with stage label, and a Start/Continue button. */

const AVATAR_GRADIENTS = [
    ['#3F57E0', '#6377F0'], ['#7C3AED', '#A78BFA'], ['#059669', '#34D399'],
    ['#DC2626', '#F87171'], ['#B45309', '#FCD34D'], ['#0369A1', '#38BDF8'],
];

function initials(name) {
    if (!name) return '?';
    const parts = String(name).trim().split(' ').filter(Boolean);
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
}

function gradientFor(name) {
    if (!name) return AVATAR_GRADIENTS[0];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_GRADIENTS[Math.abs(h) % AVATAR_GRADIENTS.length];
}

// A profile photo URL is only usable if it's a real hosted image — not a
// generic placeholder (flaticon/noun project) and not LinkedIn's default ghost
// silhouette (static.licdn.com/aero-v1/...). Expired licdn URLs 404 and are
// caught by the <img> onError fallback instead.
export function isUsablePhoto(url) {
    if (!url) return false;
    const u = String(url);
    return !u.includes('flaticon')
        && !u.includes('thenounproject')
        && !u.includes('static.licdn.com'); // LinkedIn ghost / static assets
}

export function CompanyLogoTile({ src, name, size = 36 }) {
    const [failed, setFailed] = React.useState(false);
    const isFallback = !src || src.includes('thenounproject') || src.includes('flaticon');
    const showFallback = isFallback || failed;
    const [g1, g2] = gradientFor(name);
    const id = `grad-${(name || 'co').replace(/\s+/g, '')}`;

    if (!showFallback) {
        return (
            <img
                src={src}
                alt=""
                className="object-contain rounded-md"
                style={{ width: size, height: size }}
                onError={() => setFailed(true)}
            />
        );
    }

    return (
        <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id={id} x1="0" y1="0" x2="36" y2="36" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor={g1} />
                    <stop offset="100%" stopColor={g2} />
                </linearGradient>
            </defs>
            <rect width="36" height="36" rx="8" fill={`url(#${id})`} />
            {/* Building shape */}
            <rect x="8" y="14" width="20" height="15" rx="1.5" fill="white" fillOpacity="0.25" />
            <rect x="10" y="12" width="16" height="3" rx="1" fill="white" fillOpacity="0.5" />
            {/* Windows row 1 */}
            <rect x="11" y="17" width="4" height="3" rx="0.8" fill="white" fillOpacity="0.8" />
            <rect x="16" y="17" width="4" height="3" rx="0.8" fill="white" fillOpacity="0.8" />
            <rect x="21" y="17" width="4" height="3" rx="0.8" fill="white" fillOpacity="0.8" />
            {/* Windows row 2 */}
            <rect x="11" y="22" width="4" height="3" rx="0.8" fill="white" fillOpacity="0.6" />
            <rect x="21" y="22" width="4" height="3" rx="0.8" fill="white" fillOpacity="0.6" />
            {/* Door */}
            <rect x="15.5" y="22" width="5" height="7" rx="0.8" fill="white" fillOpacity="0.9" />
        </svg>
    );
}

export function deriveProgress(completedSteps) {
    const done = completedSteps || 0;
    if (done >= 5) return { pct: 100, label: 'Complete', status: 'completed' };
    if (done === 0) return { pct: 0, label: 'Not started', status: 'not_started' };
    return { pct: (done / 5) * 100, label: `Stage ${done}/5`, status: 'in_progress' };
}

function StackAvatar({ contact, size = 30 }) {
    const [failed, setFailed] = React.useState(false);
    const photo = contact?.profile_picture || contact?.photo_url || contact?.image;
    const hasPhoto = isUsablePhoto(photo) && !failed;
    const [g1, g2] = gradientFor(contact?.name);

    if (hasPhoto) {
        return (
            <img
                src={photo}
                alt={contact.name || ''}
                className="rounded-full object-cover"
                style={{ width: size, height: size }}
                onError={() => setFailed(true)}
            />
        );
    }
    return (
        <div
            className="rounded-full grid place-items-center text-white font-bold select-none"
            style={{ width: size, height: size, fontSize: size * 0.34, background: `linear-gradient(135deg, ${g1}, ${g2})` }}
        >
            {initials(contact?.name)}
        </div>
    );
}

const SOURCE_CONFIG = {
    job:    { label: 'Job',            icon: Briefcase, bg: '#EEF3FF', color: '#3F57E0' },
    signal: { label: 'Hiring Signal',  icon: Zap,       bg: '#FFF7ED', color: '#D97706' },
    both:   { label: 'Job + Signal',   icon: Zap,       bg: '#F0FDF4', color: '#059669' },
};

function SourceBadge({ source }) {
    const cfg = SOURCE_CONFIG[source];
    if (!cfg) return null;
    const Icon = cfg.icon;
    return (
        <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold"
            style={{ background: cfg.bg, color: cfg.color }}
        >
            <Icon style={{ width: 10, height: 10 }} />
            {cfg.label}
        </span>
    );
}

function SavedBadge({ saved }) {
    return (
        <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold"
            style={saved
                ? { background: '#F0FDF4', color: '#059669' }
                : { background: '#FFF1F2', color: '#E11D48' }}
        >
            {saved ? 'Saved' : 'Unsaved'}
        </span>
    );
}

const NetPipelineRow = ({ company, onOpen }) => {
    const data = company.signalData || {};
    const contacts = company.allContacts || [];
    const count = contacts.length;
    const { pct, label, status } = deriveProgress(company.completedSteps);

    return (
        <div
            onClick={() => onOpen(company)}
            className="group bg-white rounded-2xl border border-[#E6E8EE] px-5 py-4 flex items-center gap-4 cursor-pointer transition-all hover:shadow-lg hover:border-[#C7D0F8]"
            style={{ boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}
        >
            {/* Logo tile */}
            <div className="w-12 h-12 rounded-xl bg-white border border-[#EEF1F5] grid place-items-center flex-shrink-0 overflow-hidden">
                <CompanyLogoTile src={data.companyLogo} name={data.companyName} />
            </div>

            {/* Name / role / meta */}
            <div className="flex-1 min-w-0">
                <div className="text-[15.5px] font-extrabold text-[#0F172A] tracking-tight truncate text-left">
                    {data.companyName || 'Unknown Company'}
                </div>
                {data.jobPosition && (
                    <div className="text-[13px] font-semibold text-[#3F57E0] truncate mt-0.5 text-left" title={data.jobPosition}>
                        {data.jobPosition}
                    </div>
                )}
                <div className="flex items-center gap-2.5 mt-1 flex-wrap text-[11.5px] text-[#64748B]">
                    {data.hqLocation && (
                        <span className="inline-flex items-center gap-1">
                            <MapPin style={{ width: 11, height: 11 }} /> {data.hqLocation}
                        </span>
                    )}
                    {data.hqLocation && <span className="text-[#CBD5E1]">·</span>}
                    <span className="inline-flex items-center gap-1">
                        <Users style={{ width: 11, height: 11 }} /> {count} contacts
                    </span>
                    <span className="text-[#CBD5E1]">·</span>
                    <SourceBadge source={company.source} />
                    <SavedBadge saved={company.savedJob || company.savedSignal} />
                </div>
            </div>

            {/* Avatar stack */}
            <div className="hidden md:flex flex-shrink-0">
                {contacts.slice(0, 4).map((c, i) => (
                    <div key={i} className="rounded-full border-2 border-white" style={{ marginLeft: i ? -10 : 0 }}>
                        <StackAvatar contact={c} size={30} />
                    </div>
                ))}
                {count > 4 && (
                    <div
                        className="rounded-full border-2 border-white bg-[#EEF1F5] text-[#64748B] grid place-items-center font-bold"
                        style={{ marginLeft: -10, width: 30, height: 30, fontSize: 10.5 }}
                    >
                        +{count - 4}
                    </div>
                )}
            </div>

            {/* Progress */}
            <div className="hidden sm:block w-[150px] flex-shrink-0">
                <div className="flex justify-between mb-1.5">
                    <span className="text-[9.5px] font-bold uppercase tracking-widest text-[#94A3B8]">Progress</span>
                    <span className={`text-[11px] font-bold ${pct > 0 ? 'text-[#3F57E0]' : 'text-[#94A3B8]'}`}>{label}</span>
                </div>
                <div className="h-1.5 bg-[#EEF1F5] rounded-full overflow-hidden">
                    <div className="h-full bg-[#3F57E0] rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>
            </div>

            {/* Action */}
            <button
                onClick={(e) => { e.stopPropagation(); onOpen(company); }}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12.5px] font-semibold transition-colors ${
                    status === 'not_started'
                        ? 'bg-[#3F57E0] text-white hover:bg-[#3347C8]'
                        : 'bg-white border border-[#E6E8EE] text-[#334155] hover:bg-gray-50'
                }`}
            >
                {status === 'not_started' ? 'Start' : status === 'completed' ? 'Review' : 'Continue'}
                <ArrowRight style={{ width: 13, height: 13 }} />
            </button>

        </div>
    );
};

export default NetPipelineRow;
